import { computed, inject, Injectable, ResourceRef } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { CassiopeiaDocument } from '@web/data';
import { interval, startWith, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { PusherService } from '../pusher/pusher.service';
import { DocumentService } from './document.service';

const DOCUMENT_OUTBOUND = 'DocumentOutbound';
const DOCUMENT_INBOUND = 'DocumentInbound';

const ALLOWED_DOCUMENT_TAGS = new Set([DOCUMENT_INBOUND, DOCUMENT_OUTBOUND]);
const refreshInterval = 30000;

@Injectable()
export class DocumentStore {
  #service = inject(DocumentService);
  readonly documents: ResourceRef<CassiopeiaDocument[]>;
  readonly inboundAndOutbound = computed(() =>
    this.documents.hasValue()
      ? this.documents.value().filter(document => document.tags.some(tag => ALLOWED_DOCUMENT_TAGS.has(tag)))
      : [],
  );
  readonly outbound = computed(
    () => this.documents.value()?.filter(document => document.tags.includes(DOCUMENT_OUTBOUND)) ?? [],
  );
  readonly inbound = computed(
    () => this.documents.value()?.filter(document => document.tags.includes(DOCUMENT_INBOUND)) ?? [],
  );

  constructor() {
    const pusher = inject(PusherService);

    const polling$ = interval(refreshInterval);
    this.documents = rxResource({
      defaultValue: [],
      stream: () =>
        pusher.isConnected$.pipe(
          switchMap(isConnected => {
            if (isConnected) {
              return pusher.listen('Document').pipe(catchError(() => polling$));
            }
            return polling$;
          }),
          startWith(null),
          switchMap(() => this.#service.list()),
        ),
    });
  }

  download(document: CassiopeiaDocument) {
    this.#service.downloadDocument(document);
  }
}
